/**
 * Plain functions for how the cached state of a form ("Questions") channel changes when the server
 * pushes an update over the socket. Moved out of ChatLayout's socket handlers with the same behaviour.
 * No React, no side effects.
 */

// Structural copies of ChatLayout's form types (same pattern as useFormData).
type FormQuestion = { id: string; prompt: string; kind?: string; pos: number; locked?: boolean };
export type FormState = {
  questions: FormQuestion[];
  answers: Record<string, string>;
  answersByUser?: Record<string, Record<string, string>>;
  allSubmitted?: Record<string, boolean>;
  participants?: string[];
};

const emptyFormState = (): FormState => ({ questions: [], answers: {}, answersByUser: {}, allSubmitted: {}, participants: [] });

/**
 * The key a channel's form state is stored under: the fully-qualified channel id ("space:channel").
 * A short id from the server is qualified with the space the user is currently in.
 */
export function formChannelKey(channelId: string, currentVoidId: string): string {
  return (channelId && channelId.includes(':'))
    ? channelId
    : (channelId.includes(':') ? channelId : `${currentVoidId}:${channelId}`);
}

/**
 * The server sent the channel's questions (and who has submitted / who is taking part). Replaces the
 * questions; keeps answers already cached; uses the new "submitted" map and participants when given.
 */
export function applyFormState(
  old: Record<string, FormState>,
  cid: string,
  update: { questions: FormQuestion[]; allSubmitted?: Record<string, boolean>; participants?: string[] },
): Record<string, FormState> {
  const prev = old[cid] || emptyFormState();
  return {
    ...old,
    [cid]: {
      questions: update.questions,
      answers: prev.answers || {},
      answersByUser: prev.answersByUser || {},
      allSubmitted: update.allSubmitted || prev.allSubmitted || {},
      participants: Array.isArray(update.participants) ? update.participants : prev.participants,
    },
  };
}

/**
 * Someone answered (or cleared, or started) a question.
 *  - with `answer`: stores that text;
 *  - without `answer` but `hasAnswer`: marks the question as answered, keeping any text we already have;
 *  - neither: removes their answer.
 * When the person is the signed-in user, their own answers map is updated too.
 */
export function applyFormAnswer(
  old: Record<string, FormState>,
  cid: string,
  update: { questionId: string; userId: string; answer?: string; hasAnswer?: boolean },
  selfUserId?: string,
): Record<string, FormState> {
  const { questionId, userId, answer, hasAnswer } = update;
  const cur = old[cid] || emptyFormState();
  const byUser = { ...(cur.answersByUser || {}) } as Record<string, Record<string, string>>;
  const urec = { ...(byUser[userId] || {}) };
  if (answer != null) {
    urec[questionId] = answer || '';
  } else if (hasAnswer) {
    urec[questionId] = urec[questionId] || '';
  } else {
    delete urec[questionId];
  }
  byUser[userId] = urec;
  // if it's me, also reflect in my own answers map
  const self = { ...cur.answers };
  if (selfUserId && userId === selfUserId) {
    if (answer != null) self[questionId] = answer || '';
    else if (!hasAnswer) delete self[questionId];
  }
  return { ...old, [cid]: { ...cur, answersByUser: byUser, answers: self } };
}
